import { useState, useSyncExternalStore } from 'react';
import { AlertCircle, Check, Copy, MessageSquareWarning, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { copyText } from '@/lib/clipboard';
import {
  closeErrorReport,
  errorReportFields,
  formatErrorReport,
  openErrorReport,
  useErrorReports,
  type ErrorReport,
} from '@/lib/errorReport';
import { captureWithMarker, isMarkerAvailable, subscribeMarker } from '@/lib/marker';
import { cn } from '@/lib/utils';

function TechnicalBlock({ label, text }: { label: string; text: string }) {
  return (
    <details className="rounded border border-border bg-muted/40">
      <summary className="cursor-pointer select-none px-2 py-1 text-xs font-medium text-muted-foreground">
        {label}
      </summary>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all border-t border-border px-2 py-1.5 font-mono text-[11px] leading-snug">
        {text}
      </pre>
    </details>
  );
}

/** Oorzaak, context en de knoppen om de fout te delen. */
export function ErrorReportDetails({ report }: { report: ErrorReport }) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const markerAvailable = useSyncExternalStore(subscribeMarker, isMarkerAvailable);

  const copy = async () => {
    try {
      await copyText(formatErrorReport(report));
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <div className="space-y-3 text-left text-sm">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Wat ging er mis
        </p>
        <p className="mt-0.5 break-words font-medium text-red-700">{report.cause}</p>
      </div>

      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
        {errorReportFields(report)
          .filter(([label]) => label !== 'Oorzaak')
          .map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="break-all font-mono text-foreground">{value}</dd>
            </div>
          ))}
      </dl>

      {report.request?.response && (
        <TechnicalBlock label="Serverantwoord" text={report.request.response} />
      )}
      {report.stack && <TechnicalBlock label="Technische details" text={report.stack} />}

      <div className="flex flex-wrap gap-2">
        {markerAvailable && (
          <Button size="sm" onClick={() => void captureWithMarker()}>
            <MessageSquareWarning /> Melden via Marker
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => void copy()}>
          {copyState === 'copied' ? <Check /> : <Copy />}
          {copyState === 'copied' ? 'Gekopieerd' : 'Kopieer details'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {copyState === 'failed'
          ? 'Kopiëren lukt hier niet. Maak een screenshot van dit venster.'
          : markerAvailable
            ? 'Marker maakt een screenshot van dit scherm en stuurt deze gegevens mee.'
            : 'Maak een screenshot van dit venster of plak de gekopieerde details in je bericht.'}
      </p>
    </div>
  );
}

/** Voor een pagina die door de fout niets anders kan tonen. */
export function ErrorReportCard({
  report,
  className,
}: {
  report: ErrorReport;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'w-full max-w-[28rem] rounded-lg border border-red-200 bg-white p-4 shadow-sm',
        className
      )}
    >
      <ErrorReportDetails report={report} />
    </div>
  );
}

/** Opent het foutpaneel vanuit een melding op de pagina; zonder melding niets. */
export function ErrorReportButton({
  report,
  className,
}: {
  report?: ErrorReport;
  className?: string;
}) {
  if (!report) return null;
  return (
    <button
      type="button"
      onClick={() => openErrorReport(report.code)}
      className={cn(
        'whitespace-nowrap font-medium underline underline-offset-2 hover:no-underline',
        className
      )}
    >
      Details ({report.code})
    </button>
  );
}

/** Zwevend paneel, geen modal: Marker moet het in de screenshot zien en het scherm bedienen. */
export function ErrorReportPanel() {
  const report = useErrorReports((state) =>
    state.reports.find((candidate) => candidate.code === state.openCode)
  );
  if (!report) return null;

  return (
    <section
      role="alertdialog"
      aria-labelledby="error-report-title"
      className="pointer-events-auto fixed bottom-4 left-4 right-4 z-[100] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-lg border border-red-200 bg-white shadow-xl sm:right-auto sm:w-[28rem]"
      // Een open dialoog sluit bij een klik erbuiten; het paneel hoort bij de fout in die dialoog.
      onPointerDown={(event) => event.nativeEvent.stopPropagation()}
    >
      <header className="flex items-start gap-3 border-b border-red-100 bg-red-50 px-4 py-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
        <h2
          id="error-report-title"
          className="min-w-0 flex-1 break-words font-semibold text-foreground"
        >
          {report.title}
        </h2>
        <Button variant="ghost" size="icon-sm" onClick={closeErrorReport} aria-label="Sluiten">
          <X />
        </Button>
      </header>
      <div className="px-4 py-3">
        <ErrorReportDetails key={report.code} report={report} />
      </div>
    </section>
  );
}
